import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ComboSelect } from "@/components/common/ComboSelect";
import { MoneyInput } from "@/components/common/MoneyInput";
import { api, apiError, listResource } from "@/lib/api";
import { mxn } from "@/lib/format";
import { erroresGrupo, filaVacia, sumaMontos } from "@/lib/integrantesGrupo";
import { CuadreAutorizado, diferencia } from "@/components/solicitudes/CuadreAutorizado";
import { toast } from "sonner";

const aFila = (i) => ({ ...filaVacia(i.rol), ...i, monto: i.monto ?? "", tasa_ordinaria: i.tasa_ordinaria ?? "" });

function filasIniciales(info) {
  if ((info.integrantes || []).length) return info.integrantes.map(aFila);
  return [{ ...filaVacia("titular"), cliente_id: info.cliente_id || "", cliente: info.cliente || "" }, filaVacia()];
}

/** Captura de integrantes: en la SOLICITUD (antes del Comité) o, en grupales autorizadas
 *  antes de esa regla, al CONTRATAR (`contratar`), con la suma igual al monto autorizado. */
export function IntegrantesCaptura({ info, onGuardado, contratar }) {
  const [filas, setFilas] = useState(() => filasIniciales(info));
  const [clientes, setClientes] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { listResource("clientes").then(setClientes).catch(() => {}); }, []);

  const set = (idx, patch) => setFilas((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const errs = erroresGrupo(filas);
  const cuadra = contratar && Number(info.monto_autorizado) > 0 && diferencia(sumaMontos(filas), info.monto_autorizado) === 0;
  const payload = () => filas.map((i) => ({
    cliente_id: i.cliente_id, rol: i.rol, monto: parseFloat(i.monto) || 0,
    tasa_ordinaria: i.tasa_ordinaria === "" || i.tasa_ordinaria == null ? undefined : parseFloat(i.tasa_ordinaria),
  }));

  const guardar = async () => {
    setSaving(true);
    try {
      const integrantes = payload().map((i) => ({ ...i, tasa_ordinaria: i.tasa_ordinaria ?? null }));
      const { data } = await api.put(`/solicitudes/${info.solicitud_id}/integrantes`, { integrantes });
      toast.success(data.sin_cambios ? "Sin cambios en los integrantes"
        : `Integrantes guardados · monto solicitado del grupo ${mxn(data.monto_total)}`);
      onGuardado?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="integrantes-captura">
      <div className="space-y-2">
        {filas.map((it, idx) => (
          <div key={idx} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-white p-2 sm:grid-cols-12" data-testid={`integrante-row-${idx}`}>
            <div className="sm:col-span-4">
              <Label className="mb-1 block text-[10px] uppercase text-slate-400">Cliente</Label>
              <ComboSelect testid={`integrante-cliente-${idx}`} items={clientes} value={it.cliente_id}
                onSelect={(c) => set(idx, { cliente_id: c.id, cliente: c.nombre, rfc: c.rfc })}
                getLabel={(c) => c.nombre} getSub={(c) => [c.rfc, c.tipo_persona].filter(Boolean).join(" · ")}
                getSearch={(c) => `${c.nombre || ""} ${c.rfc || ""}`}
                placeholder={it.cliente || "Selecciona cliente..."} searchPlaceholder="Buscar por nombre o RFC..." emptyText="Sin clientes." />
            </div>
            <div className="sm:col-span-3">
              <Label className="mb-1 block text-[10px] uppercase text-slate-400">Monto</Label>
              <MoneyInput data-testid={`integrante-monto-${idx}`} value={it.monto} onChange={(v) => set(idx, { monto: v })} placeholder="0.00" />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-[10px] uppercase text-slate-400">Tasa %</Label>
              <Input data-testid={`integrante-tasa-${idx}`} type="number" value={it.tasa_ordinaria}
                onChange={(e) => set(idx, { tasa_ordinaria: e.target.value })} placeholder="prod." />
            </div>
            <div className="flex items-end gap-1 sm:col-span-3">
              <Select value={it.rol} onValueChange={(v) => set(idx, { rol: v })}>
                <SelectTrigger data-testid={`integrante-rol-${idx}`} className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="titular">Titular</SelectItem>
                  <SelectItem value="obligado_solidario">Obligado</SelectItem>
                </SelectContent>
              </Select>
              {filas.length > 2 && (
                <Button size="sm" variant="ghost" className="h-9 px-2 text-red-500" data-testid={`integrante-remove-${idx}`}
                  onClick={() => setFilas((p) => p.filter((_, i) => i !== idx))}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <Button size="sm" variant="outline" onClick={() => setFilas((p) => [...p, filaVacia()])} data-testid="integrante-add">
          <Plus className="mr-1 h-4 w-4" /> Agregar integrante
        </Button>
        {!contratar && (
          <span className="text-xs font-semibold text-slate-700" data-testid="integrantes-suma">
            Monto solicitado del grupo (suma): {mxn(sumaMontos(filas))}
          </span>
        )}
      </div>
      {errs.length > 0 && (
        <p className="mt-2 text-[11px] text-amber-700" data-testid="integrantes-errores">Requisitos: {errs.join(" · ")}.</p>
      )}
      {contratar ? (
        <>
          <CuadreAutorizado suma={sumaMontos(filas)} autorizado={info.monto_autorizado} />
          <Button size="sm" className="mt-3 bg-accent hover:bg-forest-700" data-testid="contratar-grupal-btn"
            disabled={errs.length > 0 || !cuadra || contratar.busy || !contratar.puedeContratar}
            onClick={() => contratar.onContratar(payload())}>
            {contratar.busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Users className="mr-1.5 h-4 w-4" />} Validar grupo y contratar crédito grupal
          </Button>
        </>
      ) : (
        <Button size="sm" className="mt-3 bg-accent hover:bg-forest-700" data-testid="integrantes-guardar-btn"
          disabled={errs.length > 0 || saving} onClick={guardar}>
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar integrantes
        </Button>
      )}
    </div>
  );
}
